import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocationInfo } from 'src/hooks';
import _, { Dictionary } from 'lodash';
import * as types from './types';
import * as selectors from './selectors';
import * as actions from './actions';
import * as utils from './utils';
import { Field } from 'src/config';

export interface Props {
    conf: types.ListConf;
    children: (props: Scope) => React.ReactElement;
    local: boolean;
    regionKey: string;
}

export type Scope = types.ListState & {
    page: number;
    sort: Dictionary<types.SortOrder>;
    handleToggleAttr: (attr: string, field: Field) => void;
    handleSort: (attr: string) => void;
    handleChangePage: (page: number) => void;
    handleChangeRowsPerPage: (rows: number) => void;
    refresh: () => void;
    isAdmin: boolean;
}

const Provider: React.FC<Props> = ({ conf, children, local, regionKey }) => {
    const dispatch = useDispatch();
    const locationInfo = useLocationInfo();
    const { resourceName } = conf;
    const { location: { search } } = locationInfo;

    const state: types.ListState = useSelector(selectors.SelectByKey(regionKey));
    const { loading, error, response, request, hidden, query } = state;

    const page: number = useMemo<number>(
        () => utils.getResponsePage(response),
        [response]
    );

    const sort: Dictionary<types.SortOrder> = useMemo(() => {
        let directions: Dictionary<types.SortOrder> = {};
        if (response && response.order) {
            directions = response.order.reduce((acc, sortCol) => {
                if (sortCol.direction) {
                    acc[sortCol.path] = sortCol.direction;
                }
                return acc;
            }, directions);
        }
        return directions;
    }, [response]);

    useEffect(() => {
        dispatch(actions.reset());
        dispatch(actions.startInitialQuery(resourceName, local, search));
    }, [resourceName, search]);

    const refresh = () => {
        dispatch(actions.startExistingQuery(local));
    };

    const handleSort = (attr: string) => {
        const direction: types.SortOrder = sort[attr];
        let newDir: types.SortOrder | undefined = undefined;
        if (direction === undefined) {
            newDir = types.SortOrder.Asc;
        } else if (direction === types.SortOrder.Asc) {
            newDir = types.SortOrder.Desc;
        }
        dispatch(actions.setSort(attr, newDir));
    }

    const handleChangePage = (newPage: number) => {
        dispatch(actions.setPage(newPage));
    };

    const handleChangeRowsPerPage = (rows: number) => {
        dispatch(actions.setPageSize(rows));
    };

    const handleToggleAttr = (attr: string, field: Field) => {
        const { attributes } = request!;
        if (_.keys(attributes).includes(attr)) {
            dispatch(actions.setAttrs(_.fromPairs(_.toPairs(attributes).filter(pair => pair[0] !== attr))))
        } else {
            dispatch(actions.setAttrs({ ...attributes, [attr]: field }));
        }
    }

    const scope = {
        local,
        loading,
        error,
        response,
        request,
        page,
        sort,
        handleToggleAttr,
        handleSort,
        handleChangePage,
        handleChangeRowsPerPage,
        refresh,
        isAdmin: locationInfo.admin,
        hidden,
        query,
    };

    return (
        children(scope)
    );
};

export default Provider;
